/**
 * Funnel & qualification tab — funnel steps, AI score histogram,
 * top rejection reasons, abandonment by phase. Extracted from
 * AnalyticsCalls.jsx (iter29 refactor). Iso-behaviour.
 */
import { Clock, PhoneOff, Sparkles, Target } from "lucide-react";
import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { api } from "../../lib/api";
import { AiCommentaryBlock } from "./AiCommentaryBlock";
import { buildQueryParams, EmptyBlock, LoadingBlock, SectionCard } from "./shared";

export function FunnelTab({ filters }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/analytics/calls/funnel-detail", { params: buildQueryParams(filters) })
            .then(({ data }) => alive && setData(data))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [filters]);

    if (loading) return <LoadingBlock label="Chargement funnel & qualification…" />;
    if (!data) return <EmptyBlock label="Aucune donnée dans la période" />;

    const { funnel, ai_score_histogram, top_rejections, abandonment_by_phase } = data;
    return (
        <div className="space-y-5" data-testid="funnel-tab">
            {/* Detailed funnel with passage/loss + top loss reasons */}
            <SectionCard title="Funnel détaillé — Taux de passage & motifs de perte" icon={Target} testid="funnel-breakdown">
                <div className="space-y-3">
                    {funnel.map((s, i) => (
                        <div key={s.key} className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-start p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`funnel-step-${s.key}`}>
                            <div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Étape {i + 1}</div>
                                <div className="text-sm font-black text-shariti-black mt-0.5">{s.label}</div>
                                <div className="text-3xl font-black text-shariti-orange mt-1">{s.count}</div>
                            </div>
                            <div className="flex-1 space-y-1.5">
                                <div className="h-3 rounded-full bg-white overflow-hidden border border-slate-200">
                                    <div className="h-full bg-gradient-to-r from-shariti-orange to-shariti-orange-light" style={{ width: `${Math.min(s.passage_rate, 100)}%` }} />
                                </div>
                                <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                                    <span className="text-emerald-600">Passage {s.passage_rate}%</span>
                                    {i > 0 && <span className="text-rose-500">· Perte {s.loss_rate}%</span>}
                                </div>
                                {s.top_loss_reasons?.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Top motifs de perte</div>
                                        {s.top_loss_reasons.slice(0, 3).map((r) => (
                                            <div key={r.reason} className="flex items-center gap-2 text-[11px]" data-testid={`loss-reason-${s.key}-${r.reason}`}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                                <span className="font-medium text-slate-700 truncate">{r.label}</span>
                                                <span className="ml-auto font-black text-slate-500">{r.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <SectionCard title="Histogramme des scores IA /100" icon={Sparkles} testid="ai-score-histogram">
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={ai_score_histogram} margin={{ top: 8, right: 24, left: 0, bottom: 8 }} barCategoryGap="20%">
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                <XAxis dataKey="bin" tick={{ fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: "#94A3B8", fontWeight: 700 }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={{ border: "2px solid #F1F5F9", borderRadius: 12, fontSize: 12, fontWeight: 600 }} />
                                <Bar dataKey="count" name="Candidats" fill="#6366F1" radius={[4, 4, 0, 0]} maxBarSize={44} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </SectionCard>
                <SectionCard title="Top 5 motifs de rejet" icon={PhoneOff} testid="top-rejections">
                    {top_rejections.length === 0 ? (
                        <EmptyBlock label="Aucun rejet enregistré sur la période" small />
                    ) : (
                        <div className="space-y-2">
                            {top_rejections.map((r) => {
                                const max = Math.max(...top_rejections.map((x) => x.count));
                                const w = max ? (r.count / max) * 100 : 0;
                                return (
                                    <div key={r.reason} className="grid grid-cols-[1fr_auto] items-center gap-3" data-testid={`top-rejection-${r.reason}`}>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-bold text-slate-700 truncate">{r.label}</span>
                                                <span className="text-xs font-black text-shariti-orange">{r.count}</span>
                                            </div>
                                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-gradient-to-r from-rose-400 to-rose-500" style={{ width: `${w}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>
            </div>

            <SectionCard title="Abandons en cours d'entretien — par phase de la trame" icon={Clock} testid="abandonment-by-phase">
                {abandonment_by_phase.every((p) => p.count === 0) ? (
                    <EmptyBlock label="Aucun abandon détecté sur la période" small />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                        {abandonment_by_phase.map((p) => (
                            <div key={p.phase} className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-center" data-testid={`abandon-phase-${p.phase}`}>
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 leading-tight">{p.label}</div>
                                <div className="text-2xl font-black text-shariti-black">{p.count}</div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            <AiCommentaryBlock scope="funnel" filters={filters} testid="ai-commentary-funnel" />
        </div>
    );
}
